import React, { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import type { Props as ButtonProps } from 'src/components/ui/Button';
import Button from 'src/components/ui/Button';
import type { PhoneNumber } from 'src/types/__generated__/graphql';
import stopPropagation from 'src/utils/stopPropagation';
import Icon from 'src/components/ui/Icon';
import prefixPhoneNumber from 'src/utils/prefixPhoneNumber';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';

interface Props extends ButtonProps {
    phoneNumber: Pick<PhoneNumber, 'phoneCode' | 'number'>;
}

const HiddenPhoneNumberButton: React.FC<Props> = ({ phoneNumber, ...buttonProps }) => {
    const { t } = useTranslation(['company']);
    const [showPhoneNumber, setShowPhoneNumber] = useState(false);

    const phoneNumberLink = `tel:${phoneNumber.phoneCode}${phoneNumber.number.replace(/\s/g, '')}`;
    const phoneNumberToDisplay = prefixPhoneNumber(phoneNumber.phoneCode, phoneNumber.number);

    const { logEventDynamic } = useDynamicLogEvents();

    const phoneButton = useMemo(() => {
        const handleShowPhoneNumberClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            e.preventDefault();
            setShowPhoneNumber(true);
            logEventDynamic(AnalyticsEvent.SeePhone);
        };

        return (
            <Button
                startIcon={<Icon name={buttonProps.variant === 'outlined' ? 'Call' : 'CallWhite'} />}
                onMouseDown={stopPropagation}
                onClick={!showPhoneNumber ? handleShowPhoneNumberClick : undefined}
                color="primary"
                {...buttonProps}
            >
                {showPhoneNumber ? phoneNumberToDisplay : t('common:viewPhoneNumber')}
            </Button>
        );
    }, [buttonProps, logEventDynamic, phoneNumberToDisplay, showPhoneNumber, t]);

    return showPhoneNumber ? (
        <a
            href={phoneNumberLink}
            onMouseDown={stopPropagation}
            onClick={stopPropagation}
            data-target-id="phone-number-button-anchor"
        >
            {phoneButton}
        </a>
    ) : (
        phoneButton
    );
};

export default HiddenPhoneNumberButton;
